import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Period } from '@tariff/periods';
import { rateIdOf } from '@tariff/pieces';
import type { Programme, Rate } from '@tariff/authoring';
import { compileProgramme } from '@tariff/authoring';
import { priceStay } from '@tariff/engine';
import { dayOfMonth, money, monthLabel, shortDate, toDay, toIso } from '../lib/format';
import { useT } from '../lib/i18n';

/**
 * Every night of the season, at the price a guest would actually pay.
 *
 * ---------------------------------------------------------------------------
 * WHY A CALENDAR AS WELL AS THE GRID
 * ---------------------------------------------------------------------------
 *
 * The grid is accommodations × periods and it is the right tool for the job it
 * does: thirty units across a dozen periods is 360 numbers, and a calendar is
 * hopeless for that. But it can only say what a PERIOD costs, and three real
 * things fall outside that:
 *
 *   - a night that costs more at the weekend. The grid can now show two columns,
 *     but at nineteen periods that is thirty-eight columns to read;
 *   - a price for dates no period covers — a one-off event, a fortnight the park
 *     wants dearer, the week a festival is in town;
 *   - seeing the shape of a year. Which weeks are dear, where the steps fall,
 *     whether the ladder leaves a hole. That is a picture, not a table, and it is
 *     the surface yield management will eventually be steered from.
 *
 * ---------------------------------------------------------------------------
 * WHAT A DAY SHOWS
 * ---------------------------------------------------------------------------
 *
 * Not the rate's stored number — what a guest would be charged for ONE night
 * starting that day, computed by the same engine the website uses. So layering,
 * weekday restrictions and narrower-wins all show up as they really are, and a
 * band that never applies because something narrower covers it is visibly not
 * applying rather than sitting in a list looking authoritative.
 *
 * ---------------------------------------------------------------------------
 * WHAT AN EDIT WRITES
 * ---------------------------------------------------------------------------
 *
 * One rate covering the selected span, restricted to the weekdays actually
 * selected. Selecting every Saturday in July and typing 70 writes a July rate
 * for Saturdays — which is exactly what the operator meant, in one row, rather
 * than five one-day rates.
 *
 * It wins because it is NARROWER, and narrower wins in this engine even when it
 * is dearer — verified, and the reason this view can edit at all. A model where
 * the cheapest always won would make a calendar that can only ever discount.
 */

export interface NightCalendarProps {
  programme: Programme;
  /** The library, so a day can say which period it falls in. */
  periods: Period[];
  seasonYear: number;
  readOnly?: boolean;
  onChange: (next: Programme) => void;
}

interface DayCell {
  day: number;
  /**
   * What the ACCOMMODATION costs for a night starting here — not the quote
   * total.
   *
   * The total carries the tourist tax and the cleaning fee, and this is the
   * screen an operator sets rates on: a one-off EUR 60 cleaning charge baked
   * into every night of the year makes the whole calendar look flat and dear,
   * and none of it is a number anybody can edit from here. The guest-facing
   * total lives on the quote panel, where it belongs.
   */
  price: number | null;
  /** The rate that priced it, for explaining a number somebody disputes. */
  from: string | null;
  /**
   * Nights the cheapest block actually covers.
   *
   * More than one means a single night cannot be bought at all: the price shown
   * is a three-night stay trimmed to one and charged in full, which is genuinely
   * what the card says and reads as madness in a row of nightly prices unless
   * the cell admits it.
   */
  blockNights: number;
}

const WEEKDAY_NAMES = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

/**
 * How many nights the rate behind a compiled piece covers.
 *
 * Compiled piece ids carry the rate's id with a suffix when one rate compiles to
 * several placements — `zomer3#15`, `week__2026-04-01`. Both separators have to
 * come off, and a piece whose rate has since been renamed simply reads as one
 * night, which is the harmless answer.
 */
/**
 * A rate this calendar wrote, as opposed to one the card came with.
 *
 * The operator asked the question this answers: does the calendar have priority
 * over the periods? It has none, because a period is not a price — a period is
 * a named stretch of dates the panel uses for authoring, and every price lives
 * on a RATE. What the calendar writes is a narrower rate, and narrower wins.
 *
 * So the useful distinction on screen is not calendar-versus-period but
 * "the card's own price" versus "a price typed over it", and that is what the
 * `night-` prefix identifies.
 */
function isOverride(pieceId: string | null): boolean {
  return pieceId !== null && rateIdOf(pieceId).startsWith('night-');
}

function rateNightsFor(programme: Programme, pieceId: string | null): number {
  if (!pieceId) return 1;
  const rateId = rateIdOf(pieceId);
  return programme.rates.find((r) => r.id === rateId)?.nights ?? 1;
}

/** 1970-01-01 was a Thursday, so this lands Monday at index 0. */
const weekdayIndex = (day: number) => ((day + 3) % 7 + 7) % 7;

export function NightCalendar({
  programme, periods, seasonYear, readOnly, onChange,
}: NightCalendarProps) {
  const t = useT();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [anchor, setAnchor] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [cells, setCells] = useState<DayCell[] | null>(null);

  /**
   * Two ways of working, because they are two different jobs.
   *
   * `select` is for a RULE — every Saturday in July costs seventy — and it writes
   * one rate that says so. It is the right tool when the same number covers many
   * nights, and it is hopeless when thirty nights each want their own figure,
   * because that is thirty select-and-apply cycles.
   *
   * `type` is for exactly that case: put a number on a night, tab, put a number on
   * the next. Which is what yield management looks like when a person does it by
   * hand, and what a park does with a spreadsheet today.
   */
  const [mode, setMode] = useState<'select' | 'type'>('select');
  /**
   * Typed prices, by night, held until Save.
   *
   * Not written as they are typed: a night is one keystroke away from being
   * `7` on the way to `70`, and writing each intermediate value would leave the
   * card full of rates for prices nobody meant.
   */
  const [typed, setTyped] = useState<Map<number, string>>(new Map());

  const from = useMemo(() => toDay(`${seasonYear}-01-01`)!, [seasonYear]);
  const until = useMemo(() => toDay(`${seasonYear + 1}-01-01`)!, [seasonYear]);

  /**
   * Priced a night at a time, off the main thread's critical path.
   *
   * A season is 365 stays to price. Doing it in one synchronous pass blocks the
   * tab for long enough to feel broken on a laptop, so it runs in slices and the
   * calendar fills in — which also lets a change to the programme cancel a run
   * that is already half done instead of racing it.
   */
  useEffect(() => {
    let cancelled = false;
    const compiled = compileProgramme(programme);
    const out: DayCell[] = [];
    let day = from;

    const slice = () => {
      if (cancelled) return;
      const started = performance.now();
      while (day < until && performance.now() - started < 12) {
        let price: number | null = null;
        let source: string | null = null;
        let blockNights = 1;
        try {
          const q = priceStay(compiled.set, { arrival: day, nights: 1, quantities: {} });
          if (q.status === 'bookable') {
            // The accommodation only. See `DayCell.price`.
            price = q.lines.reduce((sum, l) => sum + l.price, 0);
            source = q.lines[0]?.pieceId ?? null;
            blockNights = rateNightsFor(programme, source);
          }
        } catch { /* a night nothing prices is simply blank */ }
        out.push({ day, price, from: source, blockNights });
        day += 1;
      }
      setCells([...out]);
      if (day < until) requestAnimationFrame(slice);
    };
    slice();
    return () => { cancelled = true; };
  }, [programme, from, until]);

  /** The period a night falls in, for the day's tooltip. */
  const periodOf = useCallback((day: number): Period | undefined => periods.find((p) => {
    const a = toDay(p.from);
    const b = toDay(p.until);
    return a !== null && b !== null && day >= a && day < b;
  }), [periods]);

  const click = (day: number, shift: boolean) => {
    if (readOnly) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && anchor !== null) {
        // A range, which is how a fortnight or a school holiday gets selected.
        const [lo, hi] = anchor <= day ? [anchor, day] : [day, anchor];
        for (let d = lo; d <= hi; d += 1) next.add(d);
        return next;
      }
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });
    if (!shift) setAnchor(day);
  };

  /**
   * Every night of one weekday in the season — "all the Saturdays", and off again.
   *
   * It only ever added. Clicking Sat, seeing the selection, and clicking Sat
   * again to undo it did nothing at all, so the only way back was Clear
   * selection — which also throws away every other night that was picked by
   * hand. A button that lights up has to be a button that turns off.
   */
  const selectWeekday = (index: number) => {
    if (readOnly) return;
    setSelected((prev) => {
      const next = new Set(prev);
      const days: number[] = [];
      for (let d = from; d < until; d += 1) if (weekdayIndex(d) === index) days.push(d);
      // Already all in? Then this is the undo. Partially in — some Saturdays
      // picked by hand — completes the set instead, which is what somebody
      // pressing "every Saturday" is asking for.
      const allOn = days.length > 0 && days.every((d) => next.has(d));
      for (const d of days) { if (allOn) next.delete(d); else next.add(d); }
      return next;
    });
  };

  /** Whether every night of a weekday is currently selected, for the button. */
  const weekdayOn = (index: number): boolean => {
    let any = false;
    for (let d = from; d < until; d += 1) {
      if (weekdayIndex(d) !== index) continue;
      any = true;
      if (!selected.has(d)) return false;
    }
    return any;
  };

  /**
   * Write one rate for the selection.
   *
   * The span is the selection's first and last night; the weekdays are the ones
   * actually selected. Both matter: a rate for "July, Saturdays" is one row an
   * operator can read and edit again later, where five separate one-day rates
   * are five rows nobody will ever tidy up.
   */
  const apply = () => {
    const days = [...selected].sort((a, b) => a - b);
    if (days.length === 0) return;
    const raw = amount.replace(/[^\d.,-]/g, '').replace(',', '.');
    const value = raw === '' ? null : Number(raw);

    /**
     * An empty box removes the override and gives the nights back to the card.
     *
     * There was no way to do this at all, and the operator found out the hard
     * way: wanting no day tariff over Ascension they typed 0, which does not
     * remove a price — it sets one, of nothing, and a night that costs zero is
     * a night the park is giving away.
     *
     * Zero still means zero. Empty means "as the card says".
     */
    if (value === null) {
      const covered = (r: Rate) => {
        if (!r.id.startsWith('night-')) return false;
        const a = toDay(r.active.from);
        const b = toDay(r.active.until);
        return a !== null && b !== null && days.some((d) => d >= a && d < b);
      };
      const kept = programme.rates.filter((r) => !covered(r));
      if (kept.length !== programme.rates.length) {
        onChange({ ...programme, rates: kept });
        setSelected(new Set());
      }
      return;
    }
    if (!Number.isFinite(value) || value < 0) return;

    const usedWeekdays = new Set(days.map(weekdayIndex));
    /**
     * A weekday rule only where the selection actually needs one.
     *
     * A CONTIGUOUS run — one night, a long weekend, a fortnight dragged with
     * shift — is already named exactly by its date window, so `arriveOn` would
     * be a second way of saying the same thing. It is not harmless: a rate that
     * restricts placement weekdays is excluded from `flattenNightly`, so it
     * never cuts the band beneath it, the two compete on price, and the edit
     * silently does nothing unless it lowers the price.
     *
     * That is what the operator hit. Two nights typed at €80 on a card whose
     * bands sit at €77 priced at €77, and every night edited this way added an
     * inert rate and one more "sits on top of" notice.
     */
    const contiguous = days.every((d, i) => i === 0 || d === days[i - 1] + 1);
    const everyDay = usedWeekdays.size === 7 || contiguous;
    const id = `night-${toIso(days[0])}-${toIso(days[days.length - 1] + 1)}`
      + (everyDay ? '' : `-${[...usedWeekdays].sort((a, b) => a - b).join('')}`);

    const rate: Rate = {
      id,
      label: t('calendar.rateLabel', {
        from: shortDate(days[0]), until: shortDate(days[days.length - 1]),
      }),
      role: 'nightly',
      nights: 1,
      // Half-open, like every window in this system: `until` is the first night
      // it no longer prices.
      active: { from: toIso(days[0]), until: toIso(days[days.length - 1] + 1) },
      price: { base: value },
      ...(everyDay ? {} : {
        arriveOn: [...usedWeekdays].sort((a, b) => a - b)
          .map((i) => WEEKDAY_NAMES[i]) as Rate['arriveOn'],
      }),
    };

    // Replaced by id, so setting the same selection twice corrects it rather
    // than stacking two rates that both claim those nights.
    const rest = programme.rates.filter((r) => r.id !== id);
    onChange({ ...programme, rates: [...rest, rate] });
    setSelected(new Set());
    setAmount('');
  };

  /**
   * Write the typed nights, as few rates as will say it.
   *
   * ---------------------------------------------------------------------------
   * WHY NOT ONE RATE PER NIGHT
   * ---------------------------------------------------------------------------
   *
   * Because a season typed by hand would then be three hundred and sixty-five
   * rates, and the card becomes something nobody can read, diff, or regenerate a
   * ladder against. The operator typed a number on each night; they did not ask
   * for a row each.
   *
   * So CONTIGUOUS nights carrying the same price collapse into one rate. Typing
   * 70 across a fortnight writes one fortnight-long rate; typing a different
   * number every night writes one per night, which is then genuinely what was
   * meant. The stored card is the shortest true statement of what was typed.
   *
   * Each rate covers exactly its run and beats whatever else covers those nights
   * by being narrower — the same rule the selection path relies on.
   */
  const applyTyped = () => {
    if (readOnly || typed.size === 0) return;

    const parsed: Array<{ day: number; value: number }> = [];
    for (const [day, text] of typed) {
      const raw = text.replace(/[^\d.,-]/g, '').replace(',', '.');
      if (raw === '') continue;
      const value = Number(raw);
      // A night typed as nonsense is skipped rather than stored as zero: a free
      // night and an unreadable one are different, and only one was meant.
      if (!Number.isFinite(value) || value < 0) continue;
      parsed.push({ day, value });
    }
    if (parsed.length === 0) return;
    parsed.sort((a, b) => a.day - b.day);

    const runs: Array<{ from: number; until: number; value: number }> = [];
    for (const { day, value } of parsed) {
      const last = runs[runs.length - 1];
      // Contiguous AND the same price. A gap or a different number starts a new
      // rate, because a rate is a statement about a stretch of nights.
      if (last && last.until === day && Math.abs(last.value - value) < 0.005) {
        last.until = day + 1;
        continue;
      }
      runs.push({ from: day, until: day + 1, value });
    }

    const made: Rate[] = runs.map((run) => ({
      id: `night-${toIso(run.from)}-${toIso(run.until)}`,
      label: run.until - run.from === 1
        ? shortDate(run.from)
        : t('calendar.rateLabel', { from: shortDate(run.from), until: shortDate(run.until - 1) }),
      role: 'nightly',
      nights: 1,
      active: { from: toIso(run.from), until: toIso(run.until) },
      price: { base: run.value },
    })) as Rate[];

    // Replaced by id, so retyping the same stretch corrects it instead of
    // stacking a second rate over the first.
    const ids = new Set(made.map((r) => r.id));
    const rest = programme.rates.filter((r) => !ids.has(r.id));
    onChange({ ...programme, rates: [...rest, ...made] });
    setTyped(new Map());
  };

  const months = useMemo(() => {
    const out: Array<{ key: number; label: string; days: DayCell[] }> = [];
    if (!cells) return out;
    let current: { key: number; label: string; days: DayCell[] } | null = null;
    for (const cell of cells) {
      // A NUMBER, not a sliced string. The key is only ever compared, so cutting
      // it out of an ISO date was string surgery to produce something that is not
      // a string — and the repo-wide guard against hand-cut dates was right to
      // object even though this one is never displayed. What IS displayed is the
      // month formatted in the park's own language: a heading reading "2027-07"
      // is this file talking to itself.
      const at = new Date(cell.day * 86_400_000);
      const key = at.getUTCFullYear() * 12 + at.getUTCMonth();
      if (!current || current.key !== key) {
        current = { key, label: monthLabel(cell.day), days: [] };
        out.push(current);
      }
      current.days.push(cell);
    }
    return out;
  }, [cells]);

  const prices = (cells ?? []).map((c) => c.price).filter((p): p is number => p !== null);
  const lo = prices.length ? Math.min(...prices) : 0;
  const hi = prices.length ? Math.max(...prices) : 0;

  return (
    <div className="night-cal">
      <div className="row">
        {/* The weekday buttons are the answer to "how do I price weekends":
            click Fri, click Sat, type a number. No mode to find first. */}
        <span className="note">{t('calendar.selectWeekday')}</span>
        {WEEKDAY_NAMES.map((_, i) => (
          <button
            key={WEEKDAY_NAMES[i]}
            type="button"
            className={`btn day${weekdayOn(i) ? ' on' : ''}`}
            aria-pressed={weekdayOn(i)}
            disabled={readOnly}
            onClick={() => selectWeekday(i)}
          >
            {t(`calendar.day${i}` as 'calendar.day0')}
          </button>
        ))}
        <span className="grow" />
        <button type="button" className="btn" onClick={() => setSelected(new Set())}>
          {t('calendar.clear')}
        </button>
      </div>

      <div className="row">
        {/* The two jobs, named. Not a hidden mode: which one is on changes what
            a click does, so it has to be visible while clicking. */}
        <span className="note">{t('calendar.modeLabel')}</span>
        {(['select', 'type'] as const).map((m) => (
          <button
            key={m}
            type="button"
            className={`btn${mode === m ? ' primary' : ''}`}
            aria-pressed={mode === m}
            onClick={() => { setMode(m); setSelected(new Set()); }}
          >
            {t(m === 'select' ? 'calendar.modeSelect' : 'calendar.modeType')}
          </button>
        ))}
      </div>

      {mode === 'type' && (
        <div className="row">
          <button
            type="button"
            className="btn primary"
            disabled={readOnly || typed.size === 0}
            onClick={applyTyped}
          >
            {t('calendar.saveTyped', { count: typed.size })}
          </button>
          <button
            type="button"
            className="btn"
            disabled={typed.size === 0}
            onClick={() => setTyped(new Map())}
          >
            {t('calendar.discardTyped')}
          </button>
          {/* Neighbouring nights with the same price are saved as one rate
              rather than one each — the shortest true statement of what was
              typed. Storage shape, not something to read while typing. */}
          <span className="note">{t('calendar.typeNote')}</span>
        </div>
      )}

      {mode === 'select' && (
      <div className="row">
        <label>
          {t('calendar.priceFor', { count: selected.size })}
          <input
            type="text"
            inputMode="decimal"
            className="amount"
            value={amount}
            disabled={readOnly || selected.size === 0}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="btn primary"
          disabled={readOnly || selected.size === 0 || amount.trim() === ''}
          onClick={apply}
        >
          {t('calendar.apply')}
        </button>
        {/* The way back, which did not exist. An operator who typed a price
            over a night had no way to take it off again — and the obvious
            guess, typing 0, sets a price of nothing rather than removing one. */}
        <button
          type="button"
          className="btn"
          disabled={readOnly || selected.size === 0 || amount.trim() !== ''}
          onClick={apply}
        >
          {t('calendar.restore')}
        </button>
        {/* A typed-over night beats the season band because it is the more
            specific rate, dearer or cheaper — narrower always wins. */}
        <span className="note">{t('calendar.applyNote')}</span>
        <span className="note">{t('calendar.restoreHint')}</span>
      </div>
      )}

      {cells === null && <p className="note">{t('calendar.pricing')}</p>}

      <div className="cal-months">
        {months.map((m) => (
          <div className="cal-month" key={m.key}>
            <h4>{m.label}</h4>
            <div className="cal-grid">
              {/* Which column is which day.
                  The grid has always been Monday-first and never said so, which
                  leaves the reader counting back from a date they recognise.
                  Written in the interface language, because a Dutch operator
                  reads ma-di-wo and an English one Mon-Tue-Wed. */}
              {WEEKDAY_NAMES.map((name) => (
                <span className="cal-dow" key={`dow-${name}`}>
                  {t(`day.short.${name}` as 'day.short.monday')}
                </span>
              ))}
              {/* Blanks so the first day lands under its weekday. */}
              {Array.from({ length: weekdayIndex(m.days[0].day) }, (_, i) => (
                <span className="cal-pad" key={`pad-${i}`} />
              ))}
              {m.days.map((cell) => {
                const period = periodOf(cell.day);
                const on = selected.has(cell.day);
                // Shaded by price within the season's own range, so the shape of
                // the year is visible without reading every number.
                const heat = cell.price !== null && hi > lo
                  ? (cell.price - lo) / (hi - lo) : 0;
                const overridden = isOverride(cell.from);
                const title = [
                  toIso(cell.day),
                  cell.price === null ? t('calendar.noPrice') : money(cell.price),
                  period ? t('calendar.inPeriod', { period: period.label }) : t('calendar.noPeriod'),
                  // Said in the tooltip AND marked on the cell. A fact only a
                  // hover reveals is a fact an operator scanning a season does
                  // not have: they asked which nights had been typed over and
                  // could not see it.
                  overridden ? t('calendar.overridden') : '',
                  cell.from ? t('calendar.pricedBy', { rate: cell.from }) : '',
                  cell.blockNights > 1
                    ? t('calendar.wholeBlock', { nights: cell.blockNights }) : '',
                ].filter(Boolean).join(' \u00b7 ');
                const shade = cell.price === null ? undefined
                  : { background: `color-mix(in srgb, var(--accent) ${Math.round(heat * 55)}%, var(--surface))` };

                /*
                  In TYPE mode the day IS the field.
                  
                  Not a cell that opens an editor: the operator is going along a
                  fortnight putting a number on each night, and a click-to-edit
                  step between every one of them is the difference between this
                  being usable and being a demonstration. Tab moves to the next
                  night, which is what the browser already does with inputs in
                  document order.
                */
                if (mode === 'type') {
                  const draft = typed.get(cell.day);
                  const dirty = draft !== undefined;
                  return (
                    <label
                      className={`cal-day typing${dirty ? ' dirty' : ''}`}
                      key={cell.day}
                      style={shade}
                      title={title}
                    >
                      <span className="d">{dayOfMonth(cell.day)}</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`${toIso(cell.day)}`}
                        disabled={readOnly}
                        // The typed text while typing, else the price a guest
                        // would pay — so an untouched night shows the truth
                        // rather than an empty box implying nothing is charged.
                        value={draft ?? (cell.price === null ? '' : String(cell.price))}
                        onChange={(e) => setTyped((prev) => {
                          const next = new Map(prev);
                          next.set(cell.day, e.target.value);
                          return next;
                        })}
                      />
                    </label>
                  );
                }

                return (
                  <button
                    type="button"
                    key={cell.day}
                    className={`cal-day${on ? ' on' : ''}${cell.price === null ? ' none' : ''}`
                      + `${overridden ? ' overridden' : ''}`}
                    style={shade}
                    onClick={(e) => click(cell.day, e.shiftKey)}
                    title={title}
                  >
                    <span className="d">{dayOfMonth(cell.day)}</span>
                    <span className="p">
                      {cell.price === null ? '\u2014' : money(cell.price).replace(/[^\d.,]/g, '')}
                    </span>
                    {/* A night that cannot be bought on its own says so. The
                        number is a whole block's price, and 575 sitting in a row
                        of 36s reads as a mistake until the cell admits it is
                        three nights. */}
                    {cell.blockNights > 1 && (
                      <span className="blk">{t('calendar.blockTag', { nights: cell.blockNights })}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
